import { ButtonComponent } from './button/button.component';
import { CloudComponent } from './cloud/cloud.component';
import { MultiColumnComponent } from './multi-column/multi-column.component';
import { TableElement, TableToggleElement } from './table.element';
import { ToggleComponent } from './toggle/toggle.component';
import { TranslateService } from '@ngx-translate/core';

export class TableColumn {
	column: string;
	title: string;
	isIdentifier = false;
	type: any;
	caption: string;
	valueVisible: boolean;
	icon: string;
	ignoreSort = false;
	ignoreTranslate = false;

	checker: (element: any) => boolean;
	checkerType: any;
	checkerValue: (element: any) => any;
	styles = '';
	fixedLength: number = null;
	public checked = false;
	headerCheckbox = false;
	prefixText: string;

	_sortBy: string;
	set sortBy(_sortBy: string) {
		this._sortBy = _sortBy;
	}
	get sortBy(): string {
		if (this._sortBy) {
			return this._sortBy;
		} else {
			return this.column;
		}
	}
	sortDirection = 'desc';

	dataColumns: TableColumn[];
	dataColumnSeparator: string;
	translateService: TranslateService;

	decorator: (column: TableColumn, element: TableElement) => {};
	iconDecorator: (column: TableColumn, element: TableElement) => {};
	alterDecorator: (column: TableColumn, element: TableElement) => {};

	public constructor(column: string, title?: string, isIdentifier?: boolean, valueVisible: boolean = true) {
		this.column = column;
		this.title = title;
		this.type = null;
		if (isIdentifier) {
			this.isIdentifier = isIdentifier;
		}
		this.valueVisible = valueVisible;
	}

	public withType(currentType: any): TableColumn {
		this.type = currentType;
		return this;
	}

	public withHeaderCheck(headerCheckbox: boolean): TableColumn {
		this.headerCheckbox = headerCheckbox;
		return this;
	}

	public withStyles(styles: string): TableColumn {
		this.styles = styles;
		return this;
	}

	public withFixed(fixedLength: number) {
		this.fixedLength = fixedLength;
		return this;
	}

	public withValueVisible(valueVisible: boolean): TableColumn {
		this.valueVisible = valueVisible;
		return this;
	}

	public withChecker(checker: (element: any) => boolean, checkerType?: any, checkerValue?: (element: any) => any): TableColumn {
		this.checker = checker;
		this.checkerType = checkerType;
		this.checkerValue = checkerValue;

		return this;
	}

	public withIcon(icon: string): TableColumn {
		this.icon = icon;
		return this;
	}

	withSortBy(sortBy: string): TableColumn {
		this.sortBy = sortBy;
		return this;
	}

	withSortDirection(sortDirection: string): TableColumn {
		this.sortDirection = sortDirection;
		return this;
	}

	withIgnoreSort(ignoreSort: boolean) {
		this.ignoreSort = ignoreSort;
		return this;
	}

	withDataColumns(dataColumns: TableColumn[]): TableColumn {
		this.dataColumns = dataColumns;
		return this;
	}

	withDataColumnSeparator(separator: string): TableColumn {
		this.dataColumnSeparator = separator;
		return this;
	}

	withDecorator(decorator: (_column: TableColumn, _element: TableElement) => {}) {
		this.decorator = decorator;
		return this;
	}

	withIconDecorator(decorator: (_column: TableColumn, _element: TableElement) => {}) {
		this.iconDecorator = decorator;
		return this;
	}

	withAlterDecorator(decorator: (_column: TableColumn, _element: TableElement) => {}) {
		this.alterDecorator = decorator;
		return this;
	}

	withIgnoreTranslate(ignoreTranslate: boolean) {
		this.ignoreTranslate = ignoreTranslate;
		return this;
	}

	withTranslate(translateService: TranslateService) {
		this.translateService = translateService;

		if (this.translateService) {
			if (this.dataColumns) {
				this.dataColumns.forEach(dataColumn => dataColumn.withTranslate(translateService));
			}
	
			if (this.title) {
				this.translateService.get(this.title).subscribe((text: string) => this.title = text);
			}
		}
	}

	withPrefixText(prefixText: string) {
		this.prefixText = prefixText;
		return this;
	}

	public getTitle(): string {
		if (this.title == null) {
			return this.column;
		} else {
			return this.title;
		}
	}

	public getValueVisible(): boolean {
		return this.valueVisible;
	}

	public getStyles() {
		return this.styles;
	}

	public static _getData(data: any, dataIndex: string): any {
		if (dataIndex.indexOf('.') !== -1) {
			const indexArray = dataIndex.split('.');
			let element = data;
			let index = 0;

			for (index = 0; index < indexArray.length; index++) {
				if (Array.isArray(element)) {
					element = element.map(function(item) {
						return item[indexArray[index]];
					});
				} else {
					element = element[indexArray[index]];
				}
				if (!element) {
					break;
				}
			}
			return element;
		} else {
			return data[dataIndex];
		}
	}

	public getColumnContent(column: TableColumn, element: TableElement): any {
		const content = TableColumn._getData(element.getData(), column.column);

		if (column.type === 'date') {
			if (content) {
				const current = new Date(content);
				const offsetMs = current.getTimezoneOffset() * 60 * 1000;
				const dateLocal = new Date(current.getTime() - offsetMs);
	
				return dateLocal.toISOString().slice(0, 19).replace(/-/g, '/').replace('T', ' ');	
			} else {
				return '-';
			}
		} else {
			return content;
		}
	}

	public getDataColumn(columnId: string): TableColumn {
		for (const column of this.dataColumns) {
			if (column.column === columnId) {
				return column;
			}
		}
		return null;
	}

	public getDataColumnContent(columnId: string, element: TableElement): string {
		return this.getColumnContent(this.getDataColumn(columnId), element);
	}

	public getAlterContent(element: TableElement): any {
		if (this.alterDecorator) {
			return this.alterDecorator(this, element);
		} else {
			return "";
		}
	}

	public getIcon(element: TableElement): any {
		if (this.iconDecorator) {
			return this.iconDecorator(this, element);
		} else {
			return this.icon;
		}
	}

	public getContent(element: TableElement): any {
		let content = null;

		if (this.decorator) {
			return this.decorator(this, element);
		} else {
			if (this.dataColumns) {
				this.dataColumns.forEach((column: TableColumn) => {
					const columnContent = this.getColumnContent(column, element);
					if (content) {
						content += this.dataColumnSeparator + columnContent;
					} else {
						content = columnContent;
					}
				});
			} else {
				content = this.getColumnContent(this, element);
			}
			return content;
		}
	}
}

export class IdentifierTableColumn extends TableColumn {
	public constructor(column: string, title?: string, valueVisible: boolean = true) {
		super(column, title, true, valueVisible);
	}
}

export class ButtonTableColumn extends TableColumn {
	caption: string;
	color: string;
	options: any;

	public constructor(caption: string, title?: string, color?: string, styles?: string, icon?: string) {
		super('ButtonColumn' + Math.random(), title, false);

		this.withType(ButtonComponent);
		this.withCaption(caption);
		this.withColor(color);
		this.withStyles(styles);
		this.withIcon(icon);
	}

	public withCaption(caption: string): TableColumn {
		this.caption = caption;
		return this;
	}

	public withColor(color: string): TableColumn {
		this.color = color;
		return this;
	}

	public withOptions(options: any): ButtonTableColumn {
		this.options = options;
		return this;
	}

}

export class ToggleTableColumn extends ButtonTableColumn {
	defaultState: string;
	stateCaptions: any;

	activeCaption(element: TableToggleElement): string {
		if (element.activeState()) {
			return this.stateCaptions[element.activeState()];
		} else {
			return this.stateCaptions[this.defaultState];
		}
	}

	public constructor(stateCaptions: any, defaultState: string, title?: string, color?: string, styles?: string) {
		super(stateCaptions[defaultState], title, color, styles);

		this.withType(ToggleComponent);
		this.defaultState = defaultState;
		this.stateCaptions = stateCaptions;
	}
}

export class DateTableColumn extends TableColumn {
	public constructor(column: string, title?: string) {
		super(column, title, false);

		this.withType('date');
	}
}

export class CloudTableColumn extends TableColumn {
	caption: string;

	public constructor(column: string, title?: string) {
		super(column, title, false);

		this.withType(CloudComponent);
	}

}

export class ConditionalTableColumn extends TableColumn {
	matchValue: string;
	displayColumn: TableColumn;

	public constructor(column: string, matchValue: string, displayColumn: TableColumn) {
		super(column, null, false);

		this.matchValue = matchValue;
		this.displayColumn = displayColumn;
	}

	public getContent(element: TableElement): any {
		const content = TableColumn._getData(element.getData(), this.column);

		if (content === this.matchValue) {
			return this.displayColumn.getContent(element);
		} else {
			return null;
		}
	}
}

export class MultiTableColumn extends TableColumn {
	nested: TableColumn[];

	public constructor(nested: TableColumn[], title: string) {
		super('MultiColumn' + Math.random(), title, false);

		this.withType(MultiColumnComponent);
		this.nested = nested;
	}
}



